import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Building2, Phone, Mail, MapPin, Calendar, FileText, ShoppingCart, History, Edit2, Navigation, RefreshCw, X, AlertTriangle } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { Badge } from '@/components/ui/badge';
import TaskCard from '@/components/tasks/TaskCard';
import OrderForm from '@/components/orders/OrderForm';
import LeadForm from '@/components/leads/LeadForm';
import CustomerPriceManager from '@/components/products/CustomerPriceManager';
import UmsatzPanel from '@/components/contacts/UmsatzPanel';
import ZugangPanel from '@/components/contacts/ZugangPanel';
import { getKundentypBadgeClass } from '@/lib/kundentyp';
import { getMissingData } from '@/lib/contactUtils';
import { pushOrderInBackground } from '@/lib/shopifyOrderPush';
import { generateOrderPDF, sendOrderEmail } from '@/components/orders/OrderPDFGenerator';
import { onAngebotVersendet } from '@/components/tasks/TaskStateMachine';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { notifyOrderCreated } from '@/components/notifications/notificationHelper';

export default function LeadDetailSheet({ lead: leadProp, open, onOpenChange, agentEmail }) {
  const [activeTab, setActiveTab] = useState('info');
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderType, setOrderType] = useState('Bestellung');
  const [showEditForm, setShowEditForm] = useState(false);
  const [repeatItems, setRepeatItems] = useState([]);
  const queryClient = useQueryClient();

  // Taze kontakt verisi — Zugang üretimi vb. sonrası anında güncellenir
  const { data: freshLead } = useQuery({
    queryKey: ['lead-detail', leadProp?.id],
    queryFn: () => base44.entities.Lead.get(leadProp.id),
    enabled: !!leadProp?.id && open,
  });
  const lead = freshLead || leadProp;

  const { data: tasks = [] } = useQuery({
    queryKey: ['lead-tasks', lead?.id],
    queryFn: () => base44.entities.Task.filter({ lead_id: lead?.id }, '-due_date'),
    enabled: !!lead?.id,
  });

  const { data: orders = [] } = useQuery({
    queryKey: ['lead-orders', lead?.id],
    queryFn: () => base44.entities.Order.filter({ lead_id: lead?.id }, '-created_date'),
    enabled: !!lead?.id,
  });

  const updateLeadMutation = useMutation({
    mutationFn: (data) => base44.entities.Lead.update(lead.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['agent-leads']);
      queryClient.invalidateQueries({ queryKey: ['lead-detail', leadProp?.id] });
      setShowEditForm(false);
    },
    onError: (err) => {
      toast.error('Fehler beim Speichern: ' + err.message);
    },
  });

  const createOrderMutation = useMutation({
    mutationFn: async (data) => {
      const isOffer = data.order_type === 'Angebot';

      // Create order with proper status
      const order = await base44.entities.Order.create({
        ...data,
        agent_email: agentEmail,
        order_status: isOffer ? 'pending_review' : 'completed'
      });

      // Vertriebsprozess Schritt 2: Angebot versendet →
      // Status "Interessenten" + Wiedervorlage "Nachfassen /
      // Rückmeldeanfrage" nach 2 Tagen (TaskStateMachine).
      if (isOffer) {
        try {
          await onAngebotVersendet(lead, agentEmail);
          queryClient.invalidateQueries(['agent-leads']);
          queryClient.invalidateQueries(['lead-detail', lead?.id]);
          queryClient.invalidateQueries(['lead-tasks', lead?.id]);
          toast.info('Status: „Interessenten“ — Nachfassen-To-do in 2 Tagen erstellt');
        } catch (e) {
          console.warn('Vertriebsprozess (Angebot) failed:', e);
        }
      }

      // Bestellung für den Versand an Shopify senden (Hintergrund, idempotent)
      if (!isOffer) {
        pushOrderInBackground(order.id, { orderNumber: order.order_number });
      }

      // For Bestellung, generate PDF and send email
      if (!isOffer) {
        try {
          const pdfContent = await generateOrderPDF(order);
          await sendOrderEmail(order, pdfContent);
          toast.success('Bestellung erstellt und E-Mail versendet');
        } catch (error) {
          console.error('Email error:', error);
          toast.error('Auftrag erstellt, aber E-Mail konnte nicht versendet werden');
        }
      } else {
        toast.success('Angebot erstellt und an Admin gesendet');
      }

      // Sync customer-specific prices from order items
      try {
        const existingPrices = await base44.entities.CustomerPrice.filter({ lead_id: lead.id });
        const existingMap = {};
        existingPrices.forEach(cp => { existingMap[cp.product_id] = cp; });

        for (const item of (data.items || [])) {
          const existing = existingMap[item.product_id];
          if (existing) {
            // Update if price changed
            if (existing.custom_price !== item.unit_price) {
              await base44.entities.CustomerPrice.update(existing.id, {
                custom_price: item.unit_price,
                preis: item.unit_price,
                updated_at: new Date().toISOString()
              });
            }
          } else {
            // Create new customer price record
            await base44.entities.CustomerPrice.create({
              lead_id: lead.id,
              lead_name: lead.name,
              product_id: item.product_id,
              product_name: item.product_name,
              product_sku: item.product_sku,
              original_price: item.unit_price,
              custom_price: item.unit_price,
              preis: item.unit_price,
            });
          }
        }
        queryClient.invalidateQueries(['customer-prices']);
      } catch (e) {
        console.warn('Customer price sync failed:', e);
      }

      // Notify admin about new order/offer
      notifyOrderCreated(order, agentEmail);

      return order;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['lead-orders']);
      setShowOrderForm(false);
      setRepeatItems([]);
    },
    onError: (error) => {
      toast.error('Fehler: ' + error.message);
    },
  });

  const fullAddress = [lead?.strasse, lead?.plz, lead?.ort].filter(Boolean).join(', ');

  const openNavigation = () => {
    if (fullAddress) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`, '_blank');
    }
  };

  const startOrder = (type) => {
    setOrderType(type);
    setRepeatItems([]);
    setShowOrderForm(true);
  };

  if (showEditForm) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Kontakt bearbeiten</SheetTitle>
          </SheetHeader>
          <LeadForm
            lead={lead}
            onSubmit={(data) => updateLeadMutation.mutate(data)}
            onCancel={() => setShowEditForm(false)}
          />
        </SheetContent>
      </Sheet>
    );
  }

  if (showOrderForm) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <OrderForm
            lead={lead}
            orderType={orderType}
            initialItems={repeatItems}
            onSubmit={(data) => createOrderMutation.mutate(data)}
            onCancel={() => { setShowOrderForm(false); setRepeatItems([]); }}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 z-10">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-slate-900">{lead?.name}</h2>
                <p className="text-sm text-slate-500">{lead?.branche}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={lead?.status} type="lead" />
              {/* Explicit close button — required when SheetContent has p-0 */}
              <SheetClose asChild>
                <button
                  className="rounded-full w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
                  aria-label="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </SheetClose>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowEditForm(true)}
              className="flex-1"
            >
              <Edit2 className="w-4 h-4 mr-1.5" />
              Bearbeiten
            </Button>
            {fullAddress && (
              <Button
                size="sm"
                variant="outline"
                onClick={openNavigation}
                className="flex-1"
              >
                <Navigation className="w-4 h-4 mr-1.5" />
                Route
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="p-4">
          <TabsList className="w-full grid grid-cols-6 mb-4">
            <TabsTrigger value="info" className="text-xs">Info</TabsTrigger>
            <TabsTrigger value="umsatz" className="text-xs">Umsatz</TabsTrigger>
            <TabsTrigger value="tasks" className="text-xs">Aufgaben</TabsTrigger>
            <TabsTrigger value="orders" className="text-xs">Aufträge</TabsTrigger>
            <TabsTrigger value="prices" className="text-xs">Preise</TabsTrigger>
            <TabsTrigger value="zugang" className="text-xs">Zugang</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-4 mt-0">
            {/* Kundentyp + zu pflegende Daten */}
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={getKundentypBadgeClass(lead?.kundentyp)}>
                {lead?.kundentyp || 'B2B'}
              </Badge>
              {!lead?.email && (
                <span className="text-xs text-red-600 font-medium flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4 text-red-500" /> E-Mail fehlt!
                </span>
              )}
            </div>
            {getMissingData(lead).length > 0 && (
              <Card className="p-4 border-red-200 bg-red-50">
                <h3 className="text-sm font-bold text-red-700 flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4" /> Zu pflegende Daten
                </h3>
                <ul className="space-y-1">
                  {getMissingData(lead).map((m) => (
                    <li key={m.field} className="text-xs text-red-700">
                      {m.critical ? '🔴' : '🟡'} {m.label}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Contact Info */}
            <Card className="p-4 space-y-3">
              {fullAddress && (
                <button
                  onClick={openNavigation}
                  className="flex items-start gap-3 w-full text-left hover:bg-slate-50 -mx-2 px-2 py-1 rounded"
                >
                  <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                  <span className="text-slate-700">{fullAddress}</span>
                </button>
              )}
              {lead?.telefon && (
                <a href={`tel:${lead.telefon}`} className="flex items-center gap-3 text-blue-600 hover:text-blue-700">
                  <Phone className="w-5 h-5" />
                  <span>{lead.telefon}</span>
                </a>
              )}
              {lead?.email && (
                <a href={`mailto:${lead.email}`} className="flex items-center gap-3 text-blue-600 hover:text-blue-700">
                  <Mail className="w-5 h-5" />
                  <span>{lead.email}</span>
                </a>
              )}
            </Card>

            {/* Zahlungsbedingungen — über "Bearbeiten" änderbar */}
            <Card className="p-4">
              <h3 className="font-semibold text-sm text-slate-500 mb-2">Zahlungsbedingungen</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Skonto</p>
                  <p className="font-semibold">{Number(lead?.skonto_prozent) || 0} %</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Skonto Tage</p>
                  <p className="font-semibold">{Number(lead?.skonto_tage) || 0} Tage</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Kundenrabatt</p>
                  <p className="font-semibold">{Number(lead?.kundenrabatt_prozent) || 0} %</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase">Zahlungsziel</p>
                  <p className="font-semibold">{lead?.zahlungsziel_tage ?? 5} Tage</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-3 w-full"
                onClick={() => setShowEditForm(true)}
              >
                <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                Zahlungsbedingungen bearbeiten
              </Button>
            </Card>

            {/* Order Buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => startOrder('Angebot')}
                className="h-20 flex-col bg-orange-500 hover:bg-orange-600"
              >
                <FileText className="w-6 h-6 mb-1" />
                Angebot
              </Button>
              <Button
                onClick={() => startOrder('Bestellung')}
                className="h-20 flex-col bg-emerald-500 hover:bg-emerald-600"
              >
                <ShoppingCart className="w-6 h-6 mb-1" />
                Bestellung
              </Button>
            </div>

            {/* Lieferanschrift */}
            {lead?.liefer_abweichend && (
              <Card className="p-4">
                <h3 className="font-semibold text-sm text-slate-500 mb-2 flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> Lieferanschrift
                </h3>
                <p className="text-slate-700 text-sm">
                  {[lead.liefer_strasse, [lead.liefer_plz, lead.liefer_ort].filter(Boolean).join(' '), lead.liefer_land]
                    .filter(Boolean).join(', ')}
                </p>
              </Card>
            )}

            {/* Notes */}
            {lead?.notes && (
              <Card className="p-4">
                <h3 className="font-semibold text-sm text-slate-500 mb-2">Notizen</h3>
                <p className="text-slate-700">{lead.notes}</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="umsatz" className="mt-0">
            <UmsatzPanel orders={orders} />
          </TabsContent>

          <TabsContent value="tasks" className="space-y-3 mt-0">
            {tasks.length > 0 ? (
              tasks.map(task => (
                <TaskCard key={task.id} task={task} showActions={false} />
              ))
            ) : (
              <Card className="p-6 text-center">
                <Calendar className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500">Keine Aufgaben</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="orders" className="space-y-3 mt-0">
            {orders.length > 0 ? (
              orders.map(order => (
                <Card key={order.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm text-slate-500">{order.order_number}</span>
                    <StatusBadge status={order.order_type} type="order" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      {order.created_date && format(new Date(order.created_date), 'dd. MMM yyyy', { locale: de })}
                    </span>
                    <span className="font-bold text-lg">€{order.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-slate-500 hover:text-slate-700"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOrderType(order.order_type || 'Bestellung');
                        setRepeatItems(order.items || []);
                        setShowOrderForm(true);
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Wiederholen
                    </Button>
                  </div>
                </Card>
              ))
            ) : (
              <Card className="p-6 text-center">
                <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500">Keine Aufträge</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="prices" className="mt-0">
            <CustomerPriceManager leadId={lead?.id} leadName={lead?.name} />
          </TabsContent>

          <TabsContent value="zugang" className="mt-0">
            <ZugangPanel lead={lead} />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
